import { AgentAvatar } from "@/components/molecules/agent-avatar";
import { EmptyState } from "@/components/molecules/empty-state";
import { ErrorState } from "@/components/molecules/error-state";
import { PageSkeleton } from "@/components/molecules/page-skeleton";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAgents, useDeleteAgent } from "@/features/agents/hooks/use-agents";
import type { Agent } from "@/features/agents/types";
import {
  ADDRESS_REGISTERS,
  DEFAULT_PLAYBACK_RATE,
  TEMPERAMENTS,
} from "@/features/agents/types";
import { useUser } from "@/features/auth/hooks/use-user";
import { useAvailableTools } from "@/features/tools/hooks/use-tools";
import { useTranslation } from "@/features/utils/i18n";
import { useVoiceBackgrounds } from "@/features/voice-backgrounds/hooks/use-voice-backgrounds";
import { useVoices } from "@/features/voices/hooks/use-voices";
import {
  ArrowLeftIcon,
  PencilIcon,
  RobotIcon,
  TrashIcon,
} from "@phosphor-icons/react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { PageLayout } from "../layout";
import { AgentSkillSheet } from "./components/agent-skill-sheet";
import { AgentSkills } from "./components/agent-skills";
import { AgentTestButton } from "./components/agent-test";

/**
 * An agent in one page: who it is at the top, then what it can do — every
 * module one switch, its settings one click away in a side panel.
 */
export function ViewAgentPage() {
  const { t } = useTranslation("agents");
  const { company: clientId, agentId } = useParams<{
    company: string;
    agentId: string;
  }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    data: agents = [],
    isLoading: isLoadingAgents,
    isError,
    refetch,
  } = useAgents();
  const { data: available = [] } = useAvailableTools();
  const deleteAgent = useDeleteAgent();

  const agent = agents.find((a) => a.id === agentId);

  // The open module lives in the URL (`?tool=`), so the modules page can link
  // straight to one agent's settings of it.
  const openToolId = searchParams.get("tool");
  const openTool = available.find(
    (item) => item.tool.id === openToolId && item.clientTool,
  );
  const openSkill = (toolId: string) =>
    setSearchParams({ tool: toolId }, { replace: true });
  const closeSkill = () => setSearchParams({}, { replace: true });

  const handleDelete = async () => {
    if (!agentId) return;

    try {
      await deleteAgent.mutateAsync(agentId);
      toast.success(t("agents.messages.deleted"));
      navigate(`/${clientId}/agents`);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("agents.messages.deleteError"),
      );
    }
  };

  const backTo = {
    label: t("agents.edit.backToAgents"),
    path: `/${clientId}/agents`,
  };

  if (isLoadingAgents) return <PageSkeleton width="medium" />;

  if (isError) {
    return (
      <PageLayout width="medium" title={t("agents.title")} backTo={backTo}>
        <ErrorState onRetry={refetch} />
      </PageLayout>
    );
  }

  if (!agent) {
    return <AgentNotFound backTo={backTo} />;
  }

  const soleAgent = agents.length <= 1;

  return (
    <PageLayout
      width="medium"
      title={agent.name}
      description={agent.role || t("agents.view.noRole")}
      backTo={backTo}
      actions={
        <div className="flex flex-wrap gap-2">
          <AgentTestButton agent={agent} />
          <Button
            variant="outline"
            size="icon"
            onClick={() => handleDelete()}
            confirm={t("agents.delete.confirm", { name: agent.name })}
            aria-label={t("agents.edit.deleteAgent")}
            title={t("agents.edit.deleteAgent")}
          >
            <TrashIcon className="h-4 w-4" />
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        <AgentIdentityCard agent={agent} />

        <Card>
          <CardHeader>
            <CardTitle>{t("agents.skills.title")}</CardTitle>
            <CardDescription>
              {soleAgent
                ? t("agents.skills.descriptionShared", { name: agent.name })
                : t("agents.skills.descriptionPerAgent", { name: agent.name })}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <AgentSkills agent={agent} agents={agents} onOpen={openSkill} />
          </CardContent>
        </Card>
      </div>

      <AgentSkillSheet
        item={openTool}
        agent={agent}
        soleAgent={soleAgent}
        onClose={closeSkill}
      />
    </PageLayout>
  );
}

/**
 * An agent id that matches nothing — deleted, or a link from another company.
 * Shared with the edit page, which lands on the same dead end.
 */
export function AgentNotFound({
  backTo,
}: {
  backTo: { label: string; path: string };
}) {
  const { t } = useTranslation("agents");
  const navigate = useNavigate();

  return (
    <PageLayout width="medium" title={t("agents.edit.title")} backTo={backTo}>
      <EmptyState
        icon={RobotIcon}
        title={t("agents.edit.notFound")}
        action={
          <Button variant="outline" onClick={() => navigate(backTo.path)}>
            <ArrowLeftIcon />
            {backTo.label}
          </Button>
        }
      />
    </PageLayout>
  );
}

/** Who the agent is, in a glance; the full form is behind « Modifier ». */
function AgentIdentityCard({ agent }: { agent: Agent }) {
  const { t } = useTranslation("agents");
  const navigate = useNavigate();
  const { company: clientId } = useParams<{ company: string }>();
  const { data: user } = useUser();
  const { data: voices = [] } = useVoices();
  const { data: voiceBackgrounds = [] } = useVoiceBackgrounds();

  const voice = voices.find((v) => v.id === agent.voice);
  const background = voiceBackgrounds.find(
    (bg) => bg.id === agent.background_audio,
  );
  const register = (
    ADDRESS_REGISTERS.find((r) => r.value === agent.address_register) ||
    ADDRESS_REGISTERS[0]
  ).label;
  const temperamentLabel = (
    TEMPERAMENTS.find((option) => option.value === agent.temperament) ||
    TEMPERAMENTS[0]
  ).label;
  const yesNo = (value?: boolean) =>
    value ? t("agents.view.yes") : t("agents.view.no");

  const facts: { label: string; value: string }[] = [
    {
      label: t("agents.form.voice"),
      value: voice ? voice.name : t("agents.form.voicePlaceholder"),
    },
    { label: t("agents.form.addressRegister"), value: t(register) },
    { label: t("agents.form.temperament"), value: t(temperamentLabel) },
    {
      label: t("agents.form.backgroundAudio"),
      value:
        background?.name ||
        (agent.background_audio
          ? t("agents.form.customUpload")
          : t("agents.form.none")),
    },
    {
      label: t("agents.form.canReceiveTransfers"),
      value: yesNo(agent.can_receive_transfers),
    },
    ...(user?.role === "SYSADMIN" && voice?.provider_name === "elevenlabs"
      ? [
          {
            label: t("agents.form.expressiveVoice"),
            value: yesNo(agent.expressive_voice !== false),
          },
        ]
      : []),
    ...(user?.role === "SYSADMIN"
      ? [
          {
            label: t("agents.form.debugTones"),
            value: yesNo(agent.debug_tones),
          },
          {
            label: t("agents.form.anticipatedStart"),
            value: yesNo(agent.anticipated_start),
          },
          {
            label: t("agents.form.playbackRate"),
            value: `${Math.round((agent.playback_rate ?? DEFAULT_PLAYBACK_RATE) * 100)} %`,
          },
        ]
      : []),
  ];

  return (
    <Card>
      <CardContent className="space-y-5 pt-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex min-w-0 items-center gap-4">
            <AgentAvatar
              avatarId={agent.avatar_id}
              name={agent.name}
              size="xl"
            />
            <div className="min-w-0 space-y-1">
              <p className="text-muted-foreground text-xs font-medium">
                {t("agents.form.identity")}
              </p>
              <p className="line-clamp-2 text-sm">
                {agent.identity || t("agents.view.noIdentity")}
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            className="shrink-0"
            onClick={() => navigate(`/${clientId}/agents/${agent.id}/edit`)}
          >
            <PencilIcon className="h-4 w-4" />
            {t("common.edit")}
          </Button>
        </div>

        {agent.default_greetings && (
          <div className="space-y-1">
            <p className="text-muted-foreground text-xs font-medium">
              {t("agents.form.defaultGreetings")}
            </p>
            <p className="bg-muted/50 line-clamp-3 rounded-md px-3 py-2 text-sm whitespace-pre-wrap">
              {agent.default_greetings}
            </p>
          </div>
        )}

        <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
          {facts.map((fact) => (
            <div key={fact.label} className="min-w-0 space-y-0.5">
              <dt className="text-muted-foreground text-xs font-medium">
                {fact.label}
              </dt>
              <dd className="truncate text-sm">{fact.value}</dd>
            </div>
          ))}
        </dl>
      </CardContent>
    </Card>
  );
}
